import React, { ComponentType, Key, MouseEvent, MutableRefObject, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useConditionalEventListener } from '../../hooks/useConditionalEventListener';
import { PopoverPlacement } from '../PopoverButton/PopoverButton.styles';
import {
  DropdownContainer,
  DropdownPortal,
  DropdownRow,
  DropdownText,
  DropdownTitle,
  DropdownTrigger,
  PlaceholderText,
  SelectedTextContainer,
  StyledChevronIcon,
} from './Dropdown.styles';

export interface DropdownProps<T, U extends Key> {
  title?: string;
  placeholder?: string;
  disabled?: boolean;
  placement?: PopoverPlacement;
  items: ReadonlyArray<T>;
  selectedKey: U;
  keySelector: (item: T) => U;
  portalRef?: MutableRefObject<HTMLDivElement>;
  portalComponent?: typeof DropdownPortal;
  selectionComponent?: ComponentType<{ item: T }>;
  itemComponent?: ComponentType<{ item: T; selected: boolean }>;
  onSelectionChange: (selection: U) => void;
  textSelector?: (item: T) => string;
  disabledSelector?: (item: T) => boolean;
  className?: string;
  /**
   * When this function exists, it provides support for searching the dropdown
   * by typing strings that match the text contained in the dropdown results.
   */
  searchSelector?: (item: T) => string;
}

const Dropdown = <T, U extends Key>({
  title,
  placeholder,
  disabled,
  placement = PopoverPlacement.bottom,
  items,
  selectedKey,
  keySelector,
  onSelectionChange,
  selectionComponent: Selection,
  portalComponent: PortalComponent = DropdownPortal,
  itemComponent: Item,
  portalRef = null,
  textSelector,
  disabledSelector,
  searchSelector,
  className,
}: DropdownProps<T, U>) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<string>('');
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const closeDropdown = () => {
    setIsDropdownOpen(false);
    triggerRef.current.focus();
  };

  const handleSelectionChange = (key: U) => {
    onSelectionChange(key);
    closeDropdown();
  };

  const handleMouseDown = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (
      !triggerRef?.current?.contains(target) &&
      !contentRef?.current?.contains(target) &&
      !portalRef?.current?.contains(target)
    ) {
      setIsDropdownOpen(false);
    }
  };

  const handleTab = () => {
    closeDropdown();
  };

  const handleEscape = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.stopPropagation();
    closeDropdown();
  };

  const getFocusableElements = () => {
    if (contentRef.current) {
      return contentRef.current?.querySelectorAll(
        'a[href], button, input:not([type="hidden"]):not([disabled]), textarea, select, details, [tabindex]:not([tabindex="-1"])',
      );
    } else if (portalRef.current) {
      return portalRef.current?.querySelectorAll(
        'a[href], button, input:not([type="hidden"]):not([disabled]), textarea, select, details, [tabindex]:not([tabindex="-1"])',
      );
    } else {
      return null;
    }
  };

  const handleArrowKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    } else {
      const focusableElements = getFocusableElements();
      for (let i = 0; i < focusableElements?.length; i++) {
        if (focusableElements[i] === e.target) {
          const nextPos = e.key === 'ArrowUp' ? i - 1 : i + 1;
          if (nextPos >= 0 && nextPos < focusableElements?.length) {
            const nextItem = focusableElements[nextPos] as HTMLElement;
            nextItem?.focus();
            nextItem?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
    }
  };

  const handleItemSearch = (searchString: string): void => {
    items.find((item: T, index: number) => {
      if (searchSelector?.(item).toLowerCase().includes(searchString)) {
        const focusableElements = getFocusableElements();
        (focusableElements[index] as HTMLElement).focus();
      }
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'Tab':
        handleTab();
        break;
      case 'Escape':
        handleEscape(event);
        break;
      case 'ArrowUp':
        handleArrowKey(event);
        break;
      case 'ArrowDown':
        handleArrowKey(event);
        break;
      default:
        if (event.key.length === 1) {
          if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
          }

          timerRef.current = setTimeout(() => {
            searchRef.current = '';
          }, 250);
          searchRef.current = searchRef.current + event.key;

          handleItemSearch(searchRef.current);
        }
        break;
    }
  };

  const handleOutsideScroll = (event: MouseEvent) => {
    if (
      !contentRef.current?.contains(event.target as HTMLElement) &&
      !portalRef.current?.contains(event.target as HTMLElement)
    ) {
      setIsDropdownOpen(false);
    }
  };

  useConditionalEventListener(isDropdownOpen, 'scroll', handleOutsideScroll, [contentRef.current], true);
  useConditionalEventListener(isDropdownOpen, 'mousedown', handleMouseDown);

  const DropdownItems = useMemo(() => {
    return items.map((item) => {
      const key = keySelector(item);

      return (
        <DropdownRow
          key={key}
          onClick={() => handleSelectionChange(key)}
          selected={selectedKey === key}
          disabled={disabledSelector?.(item)}
        >
          {Item ? (
            <Item item={item} selected={selectedKey === key} />
          ) : (
            <DropdownText>{textSelector?.(item)}</DropdownText>
          )}
        </DropdownRow>
      );
    });
  }, [items, selectedKey, keySelector, textSelector, handleSelectionChange]);

  const selectedItem = items.find((item) => keySelector(item) === selectedKey);
  return (
    <DropdownContainer onKeyDown={handleKeyDown} className={className} disabled={disabled}>
      {title && <DropdownTitle>{title}</DropdownTitle>}
      <DropdownTrigger
        ref={triggerRef}
        disabled={disabled}
        onClick={() => {
          setIsDropdownOpen(!isDropdownOpen);
        }}
      >
        {Selection ? (
          <Selection item={selectedItem} />
        ) : (
          <>
            <SelectedTextContainer>
              {selectedItem ? textSelector?.(selectedItem) : <PlaceholderText>{placeholder}</PlaceholderText>}
            </SelectedTextContainer>
            <StyledChevronIcon />
          </>
        )}
      </DropdownTrigger>
      <PortalComponent
        isOpen={isDropdownOpen}
        triggerRef={triggerRef}
        placement={placement}
        ref={portalRef ?? contentRef}
      >
        {DropdownItems}
      </PortalComponent>
    </DropdownContainer>
  );
};

export default styled(Dropdown)`` as typeof Dropdown;
